import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const InstallmentModal = ({ sale, onClose }) => {
    const [installments, setInstallments] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // --- NEW STATES FOR BANK/CASH SELECTION ---
    const [showPayOptions, setShowPayOptions] = useState(null); // Inst ID if selecting
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBank, setSelectedBank] = useState('');

    const fetchSchedule = useCallback(async () => {
        if (!sale?.id) return;
        try {
            setLoading(true);
            const res = await API.get(`/v1/sales/installments/${sale.id}`);
            if (res.data.success) {
                setInstallments(res.data.data);
            }
            // Fetch Bank Accounts for dropdown
            const bankRes = await API.get('/v1/accounts/heads/subheads?parent=300000'); // Assuming 300000 is Assets/Bank
            setBankAccounts(bankRes.data.data || []);
        } catch (err) {
            console.error("Fetch Error:", err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    }, [sale.id]);

    useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

    const generatePaymentReceipt = (inst, method) => {
        const doc = new jsPDF();
        const timestamp = new Date().toLocaleString();
        const receiptNo = `REC-${Math.floor(10000 + Math.random() * 90000)}`;

        doc.setFillColor(15, 23, 42); 
        doc.rect(0, 0, 210, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(26);
        doc.setFont("helvetica", "bold");
        doc.text('UNITED MOTORS', 105, 20, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`Receipt No: ${receiptNo} | Date: ${timestamp}`, 105, 36, { align: 'center' });

        doc.setTextColor(30, 41, 59);
        autoTable(doc, {
            startY: 105,
            theme: 'grid',
            headStyles: { fillColor: [16, 185, 129] },
            head: [['Description', 'Information']],
            body: [
                ['Payment Type', `Installment #${inst.installment_no}`],
                ['Amount Received', `Rs. ${Math.round(inst.amount).toLocaleString()}`],
                ['Mode of Payment', method],
                ['Status', 'Paid'],
                ['Remaining Balance', `Rs. ${Math.round(sale.remaining_balance - inst.amount).toLocaleString()}`],
            ],
        });
        doc.save(`Receipt_Inst_${inst.installment_no}.pdf`);
    };

    const handlePayment = async (inst) => {
        if (paymentMethod === 'Bank Transfer' && !selectedBank) {
            alert("Please select a bank account");
            return;
        }

        try {
            const res = await API.post('/v1/sales/installments/pay', {
                installment_id: inst.id,
                sale_id: sale.id,
                amount_paid: inst.amount,
                payment_method: paymentMethod,
                account_id: paymentMethod === 'Bank Transfer' ? selectedBank : 350000 // Cash ID
            });

            if (res.data.success) {
                generatePaymentReceipt(inst, paymentMethod);
                alert("Success: Payment processed!");
                setShowPayOptions(null);
                fetchSchedule(); 
            }
        } catch (err) {
            alert("Transaction Failed: " + (err.response?.data?.error || "Error"));
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <div style={styles.headerInfo}>
                        <div style={styles.badge}>PAYMENT MANAGER</div>
                        <h2 style={styles.mainTitle}>{sale.customer_name}</h2>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>&times;</button>
                </div>

                <div style={styles.metricsRow}>
                    <div style={styles.metric}>
                        <small style={styles.metricLabel}>Remaining Balance</small>
                        <h3 style={{color: '#e11d48', margin: 0}}>Rs. {Math.round(sale.remaining_balance).toLocaleString()}</h3>
                    </div>
                </div>

                <div style={styles.tableWrapper}>
                    {loading ? (
                        <div style={styles.loader}>Syncing...</div>
                    ) : (
                        <table style={styles.table}>
                            <thead style={styles.thead}>
                                <tr>
                                    <th style={styles.th}>No.</th>
                                    <th style={styles.th}>Due Date</th>
                                    <th style={styles.th}>Amount</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {installments.map((inst) => (
                                    <tr key={inst.id} style={styles.tr}>
                                        <td style={styles.td}><b>#{inst.installment_no}</b></td>
                                        <td style={styles.td}>{new Date(inst.due_date).toLocaleDateString('en-GB')}</td>
                                        <td style={styles.td}><b>Rs. {Math.round(inst.amount).toLocaleString()}</b></td>
                                        <td style={styles.td}>
                                            <span style={inst.status === 'Paid' ? styles.statusPaid : styles.statusPending}>
                                                {inst.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {inst.status !== 'Paid' ? (
                                                showPayOptions === inst.id ? (
                                                    <div style={styles.payBox}>
                                                        <select 
                                                            value={paymentMethod} 
                                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                                            style={styles.select}
                                                        >
                                                            <option value="Cash">Cash</option>
                                                            <option value="Bank Transfer">Bank Transfer</option>
                                                        </select>

                                                        {paymentMethod === 'Bank Transfer' && (
                                                            <select 
                                                                value={selectedBank} 
                                                                onChange={(e) => setSelectedBank(e.target.value)}
                                                                style={styles.select}
                                                            >
                                                                <option value="">Select Bank</option>
                                                                {bankAccounts.map(acc => (
                                                                    <option key={acc.SubHeadID} value={acc.SubHeadID}>{acc.SubHeadName}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                        <div style={{display:'flex', gap: '5px'}}>
                                                            <button onClick={() => handlePayment(inst)} style={styles.confirmBtn}>Confirm</button>
                                                            <button onClick={() => setShowPayOptions(null)} style={styles.cancelBtn}>Cancel</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button onClick={() => setShowPayOptions(inst.id)} style={styles.payBtn}>Collect Payment</button>
                                                )
                                            ) : (
                                                <div style={styles.paidInfo}>
                                                    <span style={{color: '#16a34a', fontWeight: 'bold'}}>Paid ✅</span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- ADD THESE NEW STYLES TO YOUR STYLES OBJECT ---
const styles = {
    // ... existing styles ...
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(8px)', padding: '20px' },
    modal: { background: '#fff', width: '100%', maxWidth: '850px', maxHeight: '90vh', borderRadius: '24px', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', border: '1px solid #e2e8f0', overflow: 'hidden' },
    header: { padding: '25px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #f1f5f9' },
    badge: { background: '#eff6ff', color: '#2563eb', padding: '4px 12px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', letterSpacing: '1px', marginBottom: '8px', display: 'inline-block' },
    mainTitle: { margin: 0, fontSize: '24px', color: '#1e293b', fontWeight: '800' },
    closeBtn: { background: '#f1f5f9', border: 'none', width: '36px', height: '36px', borderRadius: '50%', fontSize: '20px', cursor: 'pointer' },
    metricsRow: { display: 'flex', gap: '15px', padding: '20px 30px', background: '#f8fafc' },
    metric: { flex: 1, background: '#fff', padding: '15px 20px', borderRadius: '16px', border: '1px solid #e2e8f0' },
    metricLabel: { color: '#64748b', fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' },
    tableWrapper: { flex: 1, overflowY: 'auto', padding: '0 30px' },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' },
    thead: { position: 'sticky', top: 0, zIndex: 10, background: '#fff' },
    th: { textAlign: 'left', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', padding: '15px 10px' },
    tr: { background: '#fff' },
    td: { padding: '15px 10px', fontSize: '14px', borderBottom: '1px solid #f1f5f9' },
    statusPaid: { color: '#16a34a', background: '#dcfce7', padding: '5px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: '800' },
    statusPending: { color: '#d97706', background: '#fef3c7', padding: '5px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: '800' },
    payBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    payBox: { display: 'flex', flexDirection: 'column', gap: '5px', padding: '5px', background: '#f1f5f9', borderRadius: '8px' },
    select: { padding: '5px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '12px' },
    confirmBtn: { background: '#16a34a', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' },
    cancelBtn: { background: '#64748b', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' },
    paidInfo: { textAlign: 'center' },
    loader: { padding: '50px', textAlign: 'center' }
};

export default InstallmentModal;